import { useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { Alert, Button, FileUpload, Modal } from '@repo/ui'

import { useVerafiedActions } from 'hooks'
import type { VerafiedImportResult } from 'services'

const ACCEPT = { 'text/csv': ['.csv'], 'text/plain': ['.csv'] }

/**
 * VRG-734 — import the two VERAfied sheets (Guests, Episodes) exported as CSV. The files are read
 * in the browser and their TEXT is posted: nothing is uploaded to storage, and the CSVs never enter
 * a repo (the guest notes are personal). Idempotent: a row imported before is left as it has since
 * been edited, so re-running after adding rows to the sheet only adds the new ones.
 */
export const ImportSheetsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { importSheets } = useVerafiedActions()
  const [guests, setGuests] = useState<{ name: string; text: string } | null>(null)
  const [episodes, setEpisodes] = useState<{ name: string; text: string } | null>(null)
  const [result, setResult] = useState<VerafiedImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // FileUpload hands back its own items (`{ id, file, preview_url }`), not Files, and it APPENDS on
  // every drop, so the sheet is the newest item's `.file`.
  const read =
    (set: (v: { name: string; text: string } | null) => void) =>
    (items: ReadonlyArray<{ file: { name: string; text: () => Promise<string> } }>) => {
      const f = items[items.length - 1]?.file
    if (!f) return set(null)
    void f.text().then((text) => set({ name: f.name, text }))
  }

  const close = () => {
    setGuests(null)
    setEpisodes(null)
    setResult(null)
    setError(null)
    onClose()
  }

  return (
    <Modal
      title="Import the VERAfied sheets"
      open={open}
      onClose={close}
      actions={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close} disabled={importSheets.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={importSheets.isPending}
              disabled={!guests && !episodes}
              onClick={() =>
                importSheets.mutate(
                  { guests_csv: guests?.text, episodes_csv: episodes?.text },
                  { onSuccess: setResult, onError: (e) => setError((e as Error).message) },
                )
              }
            >
              Import
            </Button>
          </>
        )
      }
    >
      {result ? (
        <Stack spacing={2} mt={2}>
          <Typography variant="body2">
            Guests: {result.guests.created} added, {result.guests.existing} already here.
            {result.guests.linked_to_people ? ` ${result.guests.linked_to_people} linked to a person we already store.` : ''}
            {result.guests.linked_to_companies ? ` ${result.guests.linked_to_companies} linked to a company.` : ''}
          </Typography>
          <Typography variant="body2">
            Episodes: {result.episodes.created} added, {result.episodes.existing} already here. Guest links: {result.episode_links.created} added.
          </Typography>
          {result.guests.dropped.length > 0 && (
            <Typography variant="body3" color="text.secondary">
              Skipped: {result.guests.dropped.join('; ')}
            </Typography>
          )}
          {result.episode_links.unmatched.length > 0 && (
            <Alert severity="warning" description={`No episode titled: ${result.episode_links.unmatched.join('; ')}`} />
          )}
        </Stack>
      ) : (
        <Stack spacing={4} mt={2}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Export each sheet as CSV. The files are read here and not stored. Corrections the import makes (a name checked against its
            profile, a row split across two lines) are shown on the guest for you to confirm.
          </Typography>
          <FileUpload label="Guests sheet" description={guests ? guests.name : 'Verafied - Guests.csv'} acceptFiles={ACCEPT} onFilesChange={read(setGuests)} />
          <FileUpload
            label="Episodes sheet"
            description={episodes ? episodes.name : 'Verafied - Episodes.csv'}
            acceptFiles={ACCEPT}
            onFilesChange={read(setEpisodes)}
          />
          {error && <Alert severity="error" description={error} />}
        </Stack>
      )}
    </Modal>
  )
}
